import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import ErrorDisplay from "../components/ErrorDisplay";
import AudioPlayer from "../components/AudioPlayer";
import VideoPlayer from "../components/VideoPlayer";
import type {
  Attachment,
  NfcApiResponse,
  NfcContent,
  RawNfcResponseData,
} from "../types";
import ImagePreviewer from "../components/ImagePreviewer";
import ImageSwiper from "../components/ImageSwiper";

const isBrowser = typeof window !== "undefined";

type ResolvedNfcContent = NfcContent & {
  text: string;
  attachments: Attachment[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNfcContent(value: unknown): value is NfcContent {
  if (!isRecord(value)) return false;
  return typeof value.type === "string";
}

function sanitizeAttachments(value: unknown): Attachment[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is Attachment => {
    if (!isRecord(item)) return false;
    return (
      typeof item.name === "string" &&
      typeof item.url === "string" &&
      typeof item.contentType === "string"
    );
  });
}

function resolveNfcContent(payload: RawNfcResponseData): ResolvedNfcContent | null {
  if (!payload) return null;

  if (isNfcContent(payload)) {
    return {
      ...payload,
      text: typeof payload.text === "string" ? payload.text : "",
      attachments: sanitizeAttachments(payload.attachments ?? []),
    };
  }

  if (isRecord(payload) && "data" in payload) {
    const nested =
      (payload as { data?: RawNfcResponseData | null }).data ?? null;
    return resolveNfcContent(nested);
  }

  return null;
}

function useResolvedId() {
  const params = useParams<{ id?: string }>();
  const location = useLocation();

  return useMemo(() => {
    if (params.id) return params.id;
    if (!isBrowser) return "";
    const query = new URLSearchParams(location.search);
    return query.get("id") || "";
  }, [location.search, params.id]);
}

export default function ExplorePage() {
  const id = useResolvedId();
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle"
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [content, setContent] = useState<ResolvedNfcContent | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setStatus("error");
      setErrorMessage("NFC ID 是必需的");
      return;
    }

    const controller = new AbortController();

    async function load() {
      setStatus("loading");
      setErrorMessage("");
      try {
        const response = await fetch(`/api/nfc/content?id=${id}`, {
          headers: {
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(
            `获取 NFC 内容失败: ${response.status} ${response.statusText}`
          );
        }

        const payload: NfcApiResponse = await response.json();
        if (typeof payload?.code === "number" && payload.code !== 0) {
          throw new Error(
            payload?.message || `服务返回错误（代码: ${payload.code}）`
          );
        }

        const resolvedContent = resolveNfcContent(payload?.data ?? null);

        if (!resolvedContent) {
          throw new Error(payload?.message || "NFC 内容未找到");
        }
        if (resolvedContent.type !== "custom") {
          throw new Error(`不支持的 NFC 类型: ${resolvedContent.type}`);
        }
        setContent(resolvedContent);
        setStatus("ready");
      } catch (error) {
        if (controller.signal.aborted) return;
        setStatus("error");
        setContent(null);
        setErrorMessage(
          error instanceof Error ? error.message : "获取 NFC 内容时发生未知错误"
        );
      }
    }

    load();
    return () => controller.abort();
  }, [id]);

  if (!id) {
    return (
      <ErrorDisplay
        message="缺少 NFC ID"
        description="请通过扫描 NFC 标签或使用包含 id 参数的链接访问此页面。"
      />
    );
  }

  if (status === "error") {
    return (
      <ErrorDisplay
        message={errorMessage || "获取 NFC 内容时发生未知错误"}
        description="我们暂时无法加载内容，请稍后重试或刷新页面。"
        onRetry={isBrowser ? () => window.location.reload() : undefined}
      />
    );
  }

  if (status === "loading" || !content) {
    return (
      <div
        className="min-h-screen text-white flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/bg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <p className="text-base">内容加载中...</p>
      </div>
    );
  }

  const { text, attachments, createdAt } = content;
  const title = createdAt ? createdAt.split(" ")[0] : "";

  const primaryAttachment: Attachment | undefined = attachments.length > 0 ? attachments[0] : undefined;
  const hasAttachments = Boolean(primaryAttachment);
  const isImage = hasAttachments && primaryAttachment!.contentType.startsWith("image/");
  const isVideo = hasAttachments && primaryAttachment!.contentType.startsWith("video/");
  const isAudio = hasAttachments && primaryAttachment!.contentType.startsWith("audio/");

  return (
    <div
      className="h-dvh text-[#333] flex flex-col"
      style={{
        backgroundImage: "url('/images/bg.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "top center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <header className="py-3 text-center text-lg font-medium">{title}</header>
      <main className="z-0 flex-1 overflow-y-auto overflow-x-hidden pt-[min(8.5%,48px)] py-[min(13%,48px)] flex items-center justify-center">
        <div className="w-[92%] max-w-3xl h-full">
          <section
            className="relative w-full min-h-[500px] max-h-full flex flex-col items-center justify-center gap-5 rounded-3xl bg-[#FFE60A] pt-12 text-base"
            style={{
              backgroundImage: "url('/images/bg-nfc.png')",
              backgroundSize: "63%",
              backgroundPosition: "top right",
              backgroundRepeat: "no-repeat",
            }}
          >
            <div className="pointer-events-none absolute -top-1 left-1/2 h-56 w-[87%] -translate-x-1/2 rotate-[5deg] rounded-3xl bg-[rgba(118,106,0,0.6)] -z-10" />
            <div className="pointer-events-none absolute -bottom-4 left-1/2 h-56 w-[87%] -translate-x-1/2 rotate-[5deg] rounded-3xl bg-[rgba(118,106,0,0.6)] -z-10" />
            {text && (
              <article className="px-12 h-full max-h-[250px] w-full overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words font-medium leading-[24px]">
                {text}
              </article>
            )}
            {hasAttachments && (
              <div className="w-full">
                {isImage && <ImageSwiper carouselItems={attachments} setSelectedImage={setSelectedImage} />}
                {isAudio && primaryAttachment && <AudioPlayer attachment={primaryAttachment} />}
                {isVideo && primaryAttachment && <VideoPlayer attachment={primaryAttachment} />}
              </div>
            )}
          </section>
        </div>
      </main>

      {hasAttachments && isImage && selectedImage && (
        <ImagePreviewer
          selectedImage={selectedImage}
          carouselItems={attachments}
          onClose={() => setSelectedImage(null)}
          onChange={setSelectedImage}
        />
      )}
    </div>
  );
}
